// =====================================================
// Idle Timeout Warning Modal
// AdminMunicipal v6.0 - Security Enhancement
// Shows countdown before automatic logout
// =====================================================

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, LogOut } from 'lucide-react';

interface IdleTimeoutWarningProps {
  /** Whether to show the warning modal */
  show: boolean;
  /** Time remaining in seconds */
  remainingSeconds: number;
  /** Called when user wants to stay logged in */
  onStayLoggedIn: () => void;
  /** Called when user wants to logout immediately */
  onLogout: () => void;
}

/**
 * Warning modal shown before automatic logout due to inactivity.
 * Displays a countdown and allows user to stay logged in or logout.
 */
export const IdleTimeoutWarning: React.FC<IdleTimeoutWarningProps> = ({
  show,
  remainingSeconds,
  onStayLoggedIn,
  onLogout,
}) => {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  // Calculate urgency level for visual feedback
  const isUrgent = remainingSeconds <= 60;
  const isCritical = remainingSeconds <= 30;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
          // Prevent clicks from going through to app
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full transition-colors ${
                isCritical ? 'bg-red-100' : isUrgent ? 'bg-amber-100' : 'bg-amber-50'
              }`}>
                <Clock className={`w-6 h-6 transition-colors ${
                  isCritical ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-amber-500'
                }`} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Sesion por expirar
                </h2>
                <p className="text-sm text-gray-500">
                  Por seguridad
                </p>
              </div>
            </div>

            {/* Message */}
            <p className="text-gray-600 mb-6">
              Tu sesion se cerrara automaticamente por inactividad en:
            </p>

            {/* Countdown Timer */}
            <div className="text-center mb-6">
              <motion.div
                key={remainingSeconds}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                className={`text-5xl font-bold tabular-nums transition-colors ${
                  isCritical ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-[#5D1828]'
                }`}
              >
                {minutes}:{seconds.toString().padStart(2, '0')}
              </motion.div>
              <p className="text-sm text-gray-500 mt-2">
                {isCritical ? 'Tiempo casi agotado' : isUrgent ? 'Menos de un minuto' : 'minutos restantes'}
              </p>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-gray-200 rounded-full mb-6 overflow-hidden">
              <motion.div
                className={`h-full transition-colors ${
                  isCritical ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : 'bg-[#5D1828]'
                }`}
                initial={{ width: '100%' }}
                animate={{ width: `${(remainingSeconds / 300) * 100}%` }} // 300 = 5 minutes
                transition={{ duration: 0.5 }}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onStayLoggedIn}
                className="flex-1 py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#5D1828]/30"
              >
                Continuar trabajando
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                title="Cerrar sesion ahora"
              >
                <LogOut size={18} />
              </button>
            </div>

            {/* Hint */}
            <p className="text-xs text-gray-400 text-center mt-4">
              La sesion expira tras 30 minutos de inactividad
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
